import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Crosshair, Loader2, Move, Maximize2, Layers, Target } from "lucide-react";
import { toast } from "sonner";
import { trackEvent } from "@/App";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const VITA_SHADES = ["A1","A2","A3","A3.5","A4","B1","B2","B3","B4","C1","C2","C3","C4","D2","D3","D4"];
const SHADE_COLORS = {A1:"#F5E6C8",A2:"#EEDCB8",A3:"#E5CFA0","A3.5":"#DDC490",A4:"#D4B87C",B1:"#F2E8D0",B2:"#E8DABA",B3:"#DCCCA4",B4:"#D0BD8E",C1:"#EDE6D4",C2:"#E0D8BE",C3:"#D4CBA8",C4:"#C7BC92",D2:"#EBE2CC",D3:"#DDD4B6",D4:"#CFC6A0"};
const BOX_CONFIG = {
  reference:{label:"Reference",cssClass:"selection-box--reference",labelClass:"box-label--reference"},
  tooth:{label:"Middle",cssClass:"selection-box--tooth",labelClass:"box-label--tooth"},
  cervical:{label:"Cervical",cssClass:"selection-box--cervical",labelClass:"box-label--cervical"},
  incisal:{label:"Incisal",cssClass:"selection-box--incisal",labelClass:"box-label--incisal"},
};

export default function AnalysisScreen({ image, referenceShade, setReferenceShade, analysisMode, setAnalysisMode, onResult }) {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [imgDims, setImgDims] = useState({ width: 0, height: 0 });
  const [analyzing, setAnalyzing] = useState(false);
  const [activeBox, setActiveBox] = useState(null);
  const [dragMode, setDragMode] = useState(null);

  const [refBox, setRefBox] = useState({ x:0.05,y:0.3,width:0.15,height:0.2 });
  const [toothBox, setToothBox] = useState({ x:0.5,y:0.35,width:0.15,height:0.2 });
  const [cervicalBox, setCervicalBox] = useState({ x:0.5,y:0.6,width:0.15,height:0.15 });
  const [incisalBox, setIncisalBox] = useState({ x:0.5,y:0.15,width:0.15,height:0.15 });
  const dragStart = useRef(null); const boxStart = useRef(null);

  useEffect(() => {
    trackEvent("page_view", "analyze");
    if (!image) navigate("/capture");
  }, [image, navigate]);

  const getPointerPos = useCallback((e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return { x:0,y:0,clientX:0,clientY:0 };
    const t = e.touches ? e.touches[0] : e;
    return { x:(t.clientX-rect.left)/rect.width, y:(t.clientY-rect.top)/rect.height, clientX:t.clientX, clientY:t.clientY };
  }, []);

  const getBoxState = useCallback((bt) => ({reference:refBox,tooth:toothBox,cervical:cervicalBox,incisal:incisalBox}[bt]),[refBox,toothBox,cervicalBox,incisalBox]);
  const getBoxSetter = useCallback((bt) => ({reference:setRefBox,tooth:setToothBox,cervical:setCervicalBox,incisal:setIncisalBox}[bt]),[]);

  const handlePointerDown = useCallback((e, boxType, mode) => {
    e.preventDefault(); e.stopPropagation();
    setActiveBox(boxType); setDragMode(mode);
    const pos = getPointerPos(e);
    dragStart.current = pos; boxStart.current = { ...getBoxState(boxType) };
  }, [getPointerPos, getBoxState]);

  const handlePointerMove = useCallback((e) => {
    if (!activeBox || !dragStart.current || !boxStart.current) return;
    e.preventDefault();
    const pos = getPointerPos(e);
    const dx = pos.x - dragStart.current.x;
    const dy = pos.y - dragStart.current.y;
    const setBox = getBoxSetter(activeBox);
    const box = boxStart.current;

    if (dragMode === "move") setBox({...box,x:Math.max(0,Math.min(1-box.width,box.x+dx)),y:Math.max(0,Math.min(1-box.height,box.y+dy))});
    else if (dragMode === "resize-br") setBox({...box,width:Math.max(0.05,Math.min(1-box.x,box.width+dx)),height:Math.max(0.05,Math.min(1-box.y,box.height+dy))});
    else if (dragMode === "resize-tl") { const nX=Math.max(0,box.x+dx),nY=Math.max(0,box.y+dy); setBox({x:nX,y:nY,width:Math.max(0.05,box.width-(nX-box.x)),height:Math.max(0.05,box.height-(nY-box.y))}); }
    else if (dragMode === "resize-tr") { const nY=Math.max(0,box.y+dy); setBox({...box,y:nY,width:Math.max(0.05,Math.min(1-box.x,box.width+dx)),height:Math.max(0.05,box.height-(nY-box.y))}); }
    else if (dragMode === "resize-bl") { const nX=Math.max(0,box.x+dx); setBox({...box,x:nX,width:Math.max(0.05,box.width-(nX-box.x)),height:Math.max(0.05,Math.min(1-box.y,box.height+dy))}); }
  }, [activeBox, dragMode, getPointerPos, getBoxSetter]);

  const handlePointerUp = useCallback(() => {
    setActiveBox(null); setDragMode(null); dragStart.current = null; boxStart.current = null;
  }, []);

  useEffect(() => {
    const o = { passive: false };
    window.addEventListener("mousemove",handlePointerMove,o); window.addEventListener("mouseup",handlePointerUp);
    window.addEventListener("touchmove",handlePointerMove,o); window.addEventListener("touchend",handlePointerUp);
    return () => { window.removeEventListener("mousemove",handlePointerMove); window.removeEventListener("mouseup",handlePointerUp);
      window.removeEventListener("touchmove",handlePointerMove); window.removeEventListener("touchend",handlePointerUp); };
  }, [handlePointerMove, handlePointerUp]);

  const handleAnalyze = async () => {
    if (!referenceShade) { toast.error("Select a reference shade first"); return; }
    setAnalyzing(true);
    try {
      const payload = { image, reference_shade:referenceShade, reference_box:refBox, tooth_box:toothBox, mode:analysisMode };
      if (analysisMode === "multipoint") { payload.cervical_box = cervicalBox; payload.incisal_box = incisalBox; }
      const response = await axios.post(`${API}/analyze`, payload);
      onResult(response.data); navigate("/results");
    } catch (err) { console.error(err); toast.error("Analysis failed. Please try again."); }
    finally { setAnalyzing(false); }
  };

  const renderBox = (box, type) => {
    const c = BOX_CONFIG[type];
    return (
      <div data-testid={`selection-box-${type}`} className={`selection-box ${c.cssClass}`}
        style={{left:`${box.x*100}%`,top:`${box.y*100}%`,width:`${box.width*100}%`,height:`${box.height*100}%`}}
        onMouseDown={(e) => handlePointerDown(e,type,"move")} onTouchStart={(e) => handlePointerDown(e,type,"move")}>
        <div className={`box-label ${c.labelClass}`}>{c.label}</div>
        {["tl","tr","bl","br"].map((corner) => (
          <div key={corner} className={`resize-handle ${corner==="tl"?"top-left":corner==="tr"?"top-right":corner==="bl"?"bottom-left":"bottom-right"}`}
            onMouseDown={(e) => handlePointerDown(e,type,`resize-${corner}`)} onTouchStart={(e) => handlePointerDown(e,type,`resize-${corner}`)} />
        ))}
      </div>
    );
  };

  if (!image) return null;
  if (analyzing) return (
    <div data-testid="processing-overlay" className="min-h-screen flex flex-col items-center justify-center px-6">
      <div className="relative flex items-center justify-center mb-8"><div className="processing-ring-outer absolute"/><div className="processing-ring"/></div>
      <h2 className="text-xl font-semibold text-white mb-2 animate-fade-in">Calibrating Lighting...</h2>
      <p className="text-sm text-[#7a7894] animate-fade-in stagger-1 opacity-0">{analysisMode==="multipoint"?"Analyzing 3 tooth zones":"Applying chromatic adaptation"}</p>
    </div>
  );

  return (
    <div data-testid="analysis-screen" className="min-h-screen flex flex-col pb-6 max-w-lg mx-auto">
      <div className="flex items-center gap-3 px-5 py-4">
        <Button data-testid="back-to-capture-btn" variant="ghost" size="icon" onClick={() => navigate("/capture")}
          className="rounded-xl text-[#7a7894] hover:text-white hover:bg-[#2a273b]"><ArrowLeft className="w-5 h-5"/></Button>
        <div><h2 className="text-lg font-semibold text-white">Analysis Suite</h2><p className="text-xs text-[#5a5873]">Step 2 of 3 &mdash; Position boxes &amp; select reference</p></div>
      </div>

      {/* Mode Toggle */}
      <div className="px-5 mb-4">
        <div className="flex gap-2 p-1 rounded-xl bg-[#15132a] border border-[#33304a]">
          <button data-testid="mode-single-btn" onClick={() => setAnalysisMode("single")}
            className={`flex-1 flex items-center justify-center gap-2 h-10 rounded-lg text-sm font-medium transition-all ${analysisMode==="single"?"btn-gradient text-white":"text-[#7a7894] hover:text-white"}`}>
            <Target className="w-4 h-4"/>Single Point
          </button>
          <button data-testid="mode-multipoint-btn" onClick={() => setAnalysisMode("multipoint")}
            className={`flex-1 flex items-center justify-center gap-2 h-10 rounded-lg text-sm font-medium transition-all ${analysisMode==="multipoint"?"btn-gradient text-white":"text-[#7a7894] hover:text-white"}`}>
            <Layers className="w-4 h-4"/>Multi-Point
          </button>
        </div>
        {analysisMode==="multipoint" && <p className="text-[11px] text-[#5a5873] mt-2 text-center">3 zones: Cervical (neck), Middle (body), Incisal (tip)</p>}
      </div>

      {/* Image */}
      <div ref={containerRef} className="image-analysis-container relative mx-5 rounded-2xl overflow-hidden border border-[#33304a] bg-black">
        <img src={image} alt="Analysis" className="w-full block" onLoad={(e) => setImgDims({width:e.target.clientWidth,height:e.target.clientHeight})} draggable={false}/>
        {imgDims.width > 0 && <>{renderBox(refBox,"reference")}{renderBox(toothBox,"tooth")}{analysisMode==="multipoint" && <>{renderBox(cervicalBox,"cervical")}{renderBox(incisalBox,"incisal")}</>}</>}
      </div>

      <div className="flex items-center justify-center gap-4 mt-3 px-5">
        <div className="flex items-center gap-1.5 text-xs text-[#5a5873]"><Move className="w-3 h-3"/> Drag to move</div>
        <div className="flex items-center gap-1.5 text-xs text-[#5a5873]"><Maximize2 className="w-3 h-3"/> Corners to resize</div>
      </div>
      {analysisMode==="multipoint" && (
        <div className="flex items-center justify-center gap-3 mt-2 px-5 flex-wrap">
          {[["#A855F7","Reference"],["#FF8C42","Cervical"],["#22FF66","Middle"],["#00D4FF","Incisal"]].map(([c,l])=>(
            <div key={l} className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm" style={{backgroundColor:c}}/><span className="text-[10px] text-[#5a5873]">{l}</span></div>
          ))}
        </div>
      )}

      {/* Shade Selector */}
      <div className="px-5 mt-5 space-y-3">
        <label className="text-sm font-medium text-[#7a7894]">Declare Reference Shade Tab</label>
        <Select value={referenceShade} onValueChange={setReferenceShade}>
          <SelectTrigger data-testid="reference-shade-select" className="h-12 rounded-xl bg-[#15132a] border-[#33304a] text-white">
            <SelectValue placeholder="Select the shade tab in your photo..."/>
          </SelectTrigger>
          <SelectContent className="bg-[#1a182a] border-[#33304a]">
            {VITA_SHADES.map(s => (
              <SelectItem key={s} value={s} data-testid={`shade-option-${s}`} className="text-white hover:bg-[#2a273b] focus:bg-[#2a273b] focus:text-white">
                <div className="flex items-center gap-3"><div className="w-5 h-5 rounded-md border border-white/20" style={{backgroundColor:SHADE_COLORS[s]}}/><span className="font-medium">{s}</span></div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="grid grid-cols-8 gap-1.5">
          {VITA_SHADES.map(s => (
            <button key={s} data-testid={`shade-quick-${s}`} onClick={() => setReferenceShade(s)}
              className={`shade-swatch h-10 rounded-lg flex items-center justify-center text-[10px] font-bold border transition-all ${referenceShade===s?"active border-[#e91e63] text-[#1a182a]":"border-[#33304a] text-[#1a182a] hover:border-[#5a5873]"}`}
              style={{backgroundColor:SHADE_COLORS[s]}}>{s}</button>
          ))}
        </div>
      </div>

      <div className="px-5 mt-6">
        <Button data-testid="analyze-shade-btn" onClick={handleAnalyze} disabled={!referenceShade||analyzing}
          className="btn-gradient w-full h-14 text-base font-semibold rounded-2xl text-white border-0 disabled:opacity-40 shadow-lg shadow-[#e91e63]/20">
          {analyzing ? <><Loader2 className="w-5 h-5 mr-2 animate-spin"/>Analyzing...</> : <><Crosshair className="w-5 h-5 mr-2"/>Analyze Shade</>}
        </Button>
      </div>
    </div>
  );
}
